import styled, { css } from 'styled-components';
import { Badge, Box, Text } from '@usealloy/component-library';
import type { ContainerRelationship, NodeState } from 'flow/service/graph';
import { NODE_TEXT_MAX_WIDTH } from 'flow/service/graph';

// When showing node is included in the same snippet, show only purple border:
export const containerRelatedStyle = {
  border: '2px solid var(--cl-purple-500)',
};

// Focused node (snippet container or child action), show purple border + drop shadow:
export const containerFocusedStyle = {
  borderColor: 'var(--cl-purple-600)',
  boxShadow: `
    0px 0px 0px 4px color-mix(in srgb, var(--cl-purple-200) 80%, transparent);
  `,
};

// Focused snippet node header:
export const collapsedFocusedStyle = {
  boxShadow: `
    0px 0px 0px 0px var(--cl-purple-500),
    0px 0px 0px 0px color-mix(in srgb, var(--cl-purple-300) 70%, transparent),
    0px 4px 0px 0px color-mix(in srgb, var(--cl-purple-200) 60%, transparent)
  `,
};

export const StyledSnippetFocusable = styled.div<{
  nodeState: NodeState;
  containerRelationship: ContainerRelationship;
}>`
  ${({ nodeState }) => {
    if (nodeState === 'focused-hovered' || nodeState === 'focused') {
      return css`${{
        borderColor: 'var(--cl-purple-500)',
        borderRadius: 'var(--cl-radius-3)',
        boxShadow: `
          2px 2px 0px 0px var(--cl-purple-500),
          0px 0px 0px 4px color-mix(in srgb, var(--cl-purple-300) 70%, transparent),
          4px 4px 0px 4px color-mix(in srgb, var(--cl-purple-200) 60%, transparent)`,
      }}`;
    }
    return '';
  }}
  ${({ containerRelationship }) => {
    if (containerRelationship === 'related') {
      return css`${{
        ...containerRelatedStyle,
        borderRadius: 'var(--cl-radius-3)',
      }}`;
    }
    return '';
  }}
`;

export const collapsedSnippetStyle = {
  borderRadius: 'var(--cl-radius-3)',
  borderWidth: '1px',
  boxShadow: '0px 0px var(--cl-space-2) var(--cl-gray-400)',
};

export const expandedSnippetStyle = {
  borderTopLeftRadius: 'var(--cl-radius-3)',
  borderTopRightRadius: 'var(--cl-radius-3)',
  borderBottomLeftRadius: '0',
  borderBottomRightRadius: '0',
  borderWidth: '1px 1px 0 1px',
};

export const StyledSnippetIconContainer = styled(Box)`
  align-items: center;
  background: var(--cl-purple-400);
  border-radius: var(--cl-radius-3);
  justify-content: center;
  padding: var(--cl-space-1);
  margin-right: var(--cl-space-1);
  width: fit-content;
`;

export const StyledLabelText = styled(Text)`
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  max-width: calc(${NODE_TEXT_MAX_WIDTH}px - 26px);
`;

export const StyledVersionBadge = styled(Badge)`
  border: 1px solid var(--cl-gray-600);
  color: var(--cl-gray-800);
  font-size: var(--cl-font-size-1);
  background-color: var(--cl-gray-000);
  margin-right: var(--cl-space-1);
`;

export const StyledLabelContainer = styled(Box)`
  flex-direction: column;
  align-items: flex-start;
  max-width: 116px;
`;

export const StyledChildBackground = styled.div<{
  height: number;
  width: number;
}>`
  border-style: solid;
  border-color: var(--cl-gray-300);
  border-bottom-left-radius: var(--cl-radius-3);
  border-bottom-right-radius: var(--cl-radius-3);
  border-width: 0px 1px 1px 1px;
  background-color: var(--cl-gray-000);

  ${({ height, width }) => `
      height: ${height}px;
      width: ${width}px;
    `}
`;
